/**
 * CareerPicker — autocomplete over the curated occupation taxonomy.
 *
 * - Debounced query against `GET /api/careers/search?q=…`.
 * - Clicking a suggestion commits the canonical title (so Fast Match
 *   and the ticket both key off a known value).
 * - Pressing Enter on an unknown input is allowed — we still save it
 *   so students aren't blocked by a missing title. The backend logs
 *   it as a hint to extend the taxonomy later.
 * - Clear button nukes the field (returns to the "no destination
 *   picked" state so the free-text career_direction can shine
 *   through on the ticket).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { X } from "lucide-react";
import { api } from "@/lib/api";

export default function CareerPicker({ value, onChange, placeholder, testid }) {
  const [query, setQuery] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const containerRef = useRef(null);

  // Keep local state in sync when parent resets the value (e.g. form reload).
  useEffect(() => { setQuery(value || ""); }, [value]);

  // Debounce the search by 150ms.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      api.get(`/careers/search?q=${encodeURIComponent(query)}`)
        .then(({ data }) => {
          setItems(data?.items || []);
          setActiveIdx(0);
        })
        .catch(() => setItems([]));
    }, 150);
    return () => clearTimeout(t);
  }, [query, open]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (!containerRef.current?.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  const commit = (title) => {
    setQuery(title);
    // Send "" rather than undefined when the student commits an empty
    // string, so the backend's exclude_unset doesn't drop the key and
    // the clear actually persists. (Same bug class as the other
    // profile textarea/select handlers.)
    onChange(title || "");
    setOpen(false);
  };

  const clear = () => {
    setQuery("");
    onChange("");
    setOpen(false);
  };

  const showSuggestions = open && items.length > 0;

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        <input
          type="text"
          value={query}
          placeholder={placeholder}
          data-testid={testid}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onKeyDown={(e) => {
            if (!showSuggestions) {
              if (e.key === "Enter") {
                e.preventDefault();
                commit(query.trim());
              }
              return;
            }
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setActiveIdx((i) => Math.min(i + 1, items.length - 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setActiveIdx((i) => Math.max(i - 1, 0));
            } else if (e.key === "Enter") {
              e.preventDefault();
              const pick = items[activeIdx] || items[0];
              commit(pick?.title || query.trim());
            } else if (e.key === "Escape") {
              setOpen(false);
            }
          }}
          className="w-full border border-stone-300 rounded-md pl-3 pr-9 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
        {query && (
          <button
            type="button"
            onClick={clear}
            data-testid={`${testid}-clear`}
            aria-label="Clear destination"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      {showSuggestions && (
        <ul
          role="listbox"
          className="absolute z-30 mt-1 w-full max-h-72 overflow-auto bg-white border border-stone-200 rounded-md shadow-lg py-1"
          data-testid={`${testid}-suggestions`}
        >
          {items.map((it, i) => (
            <li
              key={it.title}
              role="option"
              aria-selected={i === activeIdx}
              onMouseEnter={() => setActiveIdx(i)}
              onMouseDown={(e) => { e.preventDefault(); commit(it.title); }}
              className={`px-3 py-2 cursor-pointer text-[13px] ${
                i === activeIdx ? "bg-[#F5F3FF] text-[#4C1D95]" : "text-stone-800"
              }`}
            >
              <div className="font-semibold">{it.title}</div>
              {it.aliases && it.aliases.length > 0 && (
                <div className="text-[11px] text-stone-500 truncate">
                  Also known as: {it.aliases.slice(0, 3).join(" · ")}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
